import React, { useState } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { message } from 'antd';

import FormSectionTitle from '../../FormSectionTitle';
import EmptyState from '../../EmptyState';
import PreviewProfileItem from '../../PreviewProfileItem';
import CustomModal from '../../../atoms/CustomModal';
import DeleteModal from '../../../atoms/DeleteModal';
import AddAwardForm from './AddAwardForm';

import { triggerChangePercentage } from '../../../../store/slices/sellerProfileSlice';
import {
  useGetBusinessAwardsQuery,
  useDeleteAwardMutation,
} from '../../../../store/services/sellerProfileCreationService';

export default function AwardsAndCertificates() {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const [modalOpen, setModalOpen] = useState(false);
  const [modalData, setModalData] = useState<any>(null);

  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [deleteId, setDeleteId] = useState<string | null>(null);

  const { sellerId } = useParams<{ sellerId: string }>();
  const profileId = sellerId || '';

  const { data: awards, refetch, isLoading } = useGetBusinessAwardsQuery({ profileId });
  const [deleteAward, { isLoading: isDeleteLoading }] = useDeleteAwardMutation();

  const handleDelete = async () => {
    if (deleteId) {
      try {
        await deleteAward({ awardId: deleteId });
        refetch();
        dispatch(triggerChangePercentage());
        setDeleteModalOpen(false);
      } catch (err) {
        message.error(t('components.somethingWentWrong'));
      }
    }
  };

  return (
    <div className="flex flex-col gap-10 pb-10" data-testid="profile-creation-awards-container">
      <div className="awards" data-testid="profile-creation-awards-section">
        <FormSectionTitle
          title={t('components.awards_and_certificates')}
          subTitle={t('components.awards_certificates_desc')}
          buttonText={t('components.add_award')}
          buttonId="add_awards_btn"
          onClick={() => {
            setModalData(null);
            setModalOpen(true);
          }}
        />

        {isLoading ? (
          <p className="dark:text-dark-text-primary" data-testid="profile-creation-awards-loading">{t('components.loading')}</p>
        ) : awards?.payload?.length ? (
          <div className="flex flex-col gap-4">
            {awards.payload.map((item: any) => (
              <PreviewProfileItem
                key={item.id}
                itemId={item.id}
                title={item.awardName}
                date={`${t('components.award_year')} ${item.awardYear}`}
                description={item.description}
                isEditable
                isDelete
                listAttachmentsMode
                attachments={item.attachments?.map((att: any) => ({
                  url: att.url,
                  type: att.type,
                  id: att.id,
                  name: att.name || att.url?.split('/').pop(),
                }))}
                onEdit={() => {
                  setModalData(item);
                  setModalOpen(true);
                }}
                onDelete={() => {
                  setDeleteId(item.id);
                  setDeleteModalOpen(true);
                }}
              />
            ))}
          </div>
        ) : (
          <EmptyState onAdd={() => {
            setModalData(null);
            setModalOpen(true);
          }} />
        )}
      </div>

      <CustomModal
        open={modalOpen}
        onCancel={() => setModalOpen(false)}
        title={t('components.add_awards_certificates')}
        centered
        withoutLogo={false}
        variant="supplier"
        description={t('components.add_awards_certificates_desc')}
      >
        {modalOpen && (
          <AddAwardForm
            profileId={profileId}
            initData={modalData}
            onSuccess={() => {
              setModalOpen(false);
              refetch();
              dispatch(triggerChangePercentage());
            }}
          />
        )}
      </CustomModal>

      <DeleteModal
        isOpen={deleteModalOpen}
        onClose={() => setDeleteModalOpen(false)}
        onDelete={handleDelete}
        isLoading={isDeleteLoading}
        title={t('components.deleteAwardTitle')}
        description={t('components.deleteAwardDescription')}
      />
    </div>
  );
}
